import '@/utils/polyfills'
import '@/utils/set-client-public-path'

import { QueryClient, VueQueryPlugin, hydrate } from '@tanstack/vue-query'
import { createApp as createClientSideApp, createSSRApp } from 'vue'
import { createWebHistory } from 'vue-router'

import App from '@/App.vue'
import { createConfigPlugin } from '@/composables/config'
import { createI18nWithAddon } from '@/i18n/i18n'
import createPinia from '@/pinia'
import createRouter from '@/router'
import { VueHeadMixin, createHead } from '@/utils/head/client'

import { createConfigLocalizedRouters } from './composables/localized-routers'
import { resolveSupportedLocale } from './i18n/helpers'
import { createQueryPersisterPlugin } from './query/persister-plugin'
import { DEFAULT_QUERIES_OPTIONS } from './query/shared'
import { SSR_QUERY } from './router/query-params'
import { errorComponentHandler, setupSentry } from './sentry'
import { getCurrentLocaleFromCookies } from './utils/cookie-parser'
import { idleCallbackPromise } from './utils/helpers'
import { isKidsHost } from './utils/is-kids-host'
import { setupCrashTracking } from './utils/reporting'
import { createApiRequestsPlugin } from './utils/requests/api-plugin'
import { ApiRequests } from './utils/requests/api-requests'
import { fetchRequestBuilder } from './utils/requests/fetch-requests'
import safeURLParse from './utils/safeURLParse'

const run = async () => {
    // Necessary to init router before setting crash tracking, otherwise vue router bugs
    // https://github.com/vuejs/router/issues/366
    const routerHistory = createWebHistory()

    let appConfig = window.additionalServerData?.config

    // this is to simulate SSR redirects in development
    let detectedLocale = window.additionalServerData?.locale.detectedLocale

    if (process.env.DEV_CONFIG) {
        appConfig = (await import('@/modules/config/createConfig')).createVueAppConfig(process.env.DEV_CONFIG)

        // to test specific locale you can hardcode it to any lang-code in BCP47 standard format
        const locale = getCurrentLocaleFromCookies(document.cookie)
        const resolvedLocale = resolveSupportedLocale(locale)
        if (resolvedLocale) {
            detectedLocale = resolvedLocale
        }
    }
    if (!appConfig) {
        throw new Error('No app config provided')
    }

    const createApp = process.env.NODE_ENV === 'production' ? createSSRApp : createClientSideApp
    const app = createApp(App)

    if (!isKidsHost(window.location.host)) {
        setupCrashTracking()
        setupSentry(appConfig.SENTRY_DSN)
        app.config.errorHandler = (err, instance, info) => errorComponentHandler(err, instance, info)
    }

    await idleCallbackPromise()

    const configPlugin = createConfigPlugin(appConfig)
    const head = createHead()

    const { i18nAddon } = createI18nWithAddon({
        enableI18n: appConfig.VUE_APP_ENABLE_I18N,
        detectedLocale,
    })

    let resolveRouterInit = () => {}
    const deferredRouterPromise = new Promise<void>((resolve) => {
        resolveRouterInit = () => resolve()
    })
    const { router, routersByLocale } = createRouter({
        history: routerHistory,
        i18nAddon,
        registerImport: (x) => () => x(),
        deferredInit: deferredRouterPromise,
        host: window.location.host,
    })
    const url = safeURLParse(location.href)
    const backendFlags = url?.searchParams.get(SSR_QUERY.BACKEND_FLAGS) ?? ''

    // easier debug for backend folks
    window.__apiRequests ||= []
    const apiPlugin = createApiRequestsPlugin(
        new ApiRequests({
            request: fetchRequestBuilder({
                apiHost: appConfig.API_HOST,
                backendFlags,
                apiRequestsRecords: window.__apiRequests,
            }),
            cmsRequest: fetchRequestBuilder({ apiHost: '', apiRequestsRecords: window.__apiRequests }),
        }),
    )
    const localizedRoutersPlugin = createConfigLocalizedRouters(routersByLocale)
    const pinia = createPinia()
    pinia.state.value = window.__pinia || {}

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                ...DEFAULT_QUERIES_OPTIONS,
                staleTime: Infinity,
            },
        },
    })
    if (window.__query) {
        hydrate(queryClient, window.__query)
    }
    await idleCallbackPromise()

    // no client-size persistent caching
    const persisterPlugin = createQueryPersisterPlugin(undefined)

    app.use(configPlugin)
        .use(apiPlugin)
        .use(i18nAddon)
        .use(router)
        .use(localizedRoutersPlugin)
        .use(pinia)
        .use(persisterPlugin)
        .use(VueQueryPlugin, { queryClient, enableDevtoolsV6Plugin: true })
        .use(head)
        .mixin(VueHeadMixin)

    app.config.performance = true

    await router.isReady()

    await idleCallbackPromise()

    app.mount('#app')
    resolveRouterInit()
}

run()
